/**
 * Deutsche Feiertage (Datenanreicherung Welle A) — rein deterministisch, kein Netzwerk-Call.
 *
 * Deutsche Feiertage sind vollstaendig berechenbar (Gauss'sche/Meeus-Osterformel + feste
 * Offsets + Bundesland-Regeln) — robuster als eine Abhaengigkeit von einer Dritt-API fuer ein
 * so grundlegendes Kalender-Feature. Wird direkt vom Frontend importiert (apps/web/src/pages/
 * calendar.tsx), kein Backend-Roundtrip.
 *
 * WICHTIG (akzeptierte Einschraenkung, kein Bug): Fronleichnam und Mariae Himmelfahrt sind in
 * der Realitaet teils nur GEMEINDE-Ebene (mehrheitlich katholische Gemeinden in z.B. Sachsen/
 * Thueringen bzw. in bayerischen Gemeinden bei Mariae Himmelfahrt), nicht landesweit. Diese
 * Funktion rechnet bewusst nur auf Bundesland-Ebene — eine feingranulare Gemeinde-Datenbank
 * waere fuer den Kalender-Anwendungsfall (Anzeige im Betriebs-Kalender) unverhaeltnismaessig.
 */

export interface Holiday {
  /** ISO-Datum (YYYY-MM-DD). */
  date: string;
  name: string;
  /** true = gilt bundesweit; false = nur im uebergebenen Bundesland. */
  nationwide: boolean;
}

export interface Bundesland {
  code: string;
  name: string;
}

/** Die 16 deutschen Bundeslaender (ISO 3166-2:DE-Kuerzel) + Anzeigename. */
export const BUNDESLAENDER: Bundesland[] = [
  { code: "BW", name: "Baden-Württemberg" },
  { code: "BY", name: "Bayern" },
  { code: "BE", name: "Berlin" },
  { code: "BB", name: "Brandenburg" },
  { code: "HB", name: "Bremen" },
  { code: "HH", name: "Hamburg" },
  { code: "HE", name: "Hessen" },
  { code: "MV", name: "Mecklenburg-Vorpommern" },
  { code: "NI", name: "Niedersachsen" },
  { code: "NW", name: "Nordrhein-Westfalen" },
  { code: "RP", name: "Rheinland-Pfalz" },
  { code: "SL", name: "Saarland" },
  { code: "SN", name: "Sachsen" },
  { code: "ST", name: "Sachsen-Anhalt" },
  { code: "SH", name: "Schleswig-Holstein" },
  { code: "TH", name: "Thüringen" },
];

const BUNDESLAND_CODES = new Set(BUNDESLAENDER.map((b) => b.code));

/**
 * Ostersonntag (gregorianisch) nach der Gauss'schen Osterformel (Meeus/Jones/Butcher-Variante).
 * UTC-Datum, damit Zeitzonenverschiebungen keine Tagesabweichung verursachen koennen.
 */
export function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = Maerz, 4 = April
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}

function addDays(base: Date, days: number): Date {
  const d = new Date(base);
  d.setUTCDate(d.getUTCDate() + days);
  return d;
}

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Buss- und Bettag: Mittwoch VOR dem 23. November (nicht: der 23. selbst, falls der zufaellig
 * ein Mittwoch ist — dann gilt die Vorwoche). Nur noch gesetzlicher Feiertag in Sachsen (seit
 * der bundesweiten Abschaffung 1995 zur Gegenfinanzierung der Pflegeversicherung).
 */
export function bussUndBettag(year: number): Date {
  const nov23 = new Date(Date.UTC(year, 10, 23));
  const dow = nov23.getUTCDay(); // 0=So .. 3=Mi .. 6=Sa
  let diff = (dow - 3 + 7) % 7;
  if (diff === 0) diff = 7; // faellt der 23. selbst auf Mittwoch, gilt die Vorwoche
  return addDays(nov23, -diff);
}

/**
 * Feiertage eines Jahres fuer ein Bundesland (bundesweite + regionale), aufsteigend sortiert.
 * `bundesland` ist eines der 16 Kuerzel aus BUNDESLAENDER (case-insensitiv); unbekannte/leere
 * Werte liefern nur die bundesweiten Feiertage (kein Fehler, keine Annahme).
 */
export function germanHolidays(year: number, bundesland: string): Holiday[] {
  const code = (bundesland || "").toUpperCase();
  const known = BUNDESLAND_CODES.has(code) ? code : null;
  const easter = easterSunday(year);

  const list: Holiday[] = [];
  const add = (date: string, name: string, nationwide: boolean, states?: string[]) => {
    if (nationwide || (known && states && states.includes(known))) {
      list.push({ date, name, nationwide });
    }
  };
  const fixed = (month: number, day: number) => iso(new Date(Date.UTC(year, month - 1, day)));
  const relEaster = (offsetDays: number) => iso(addDays(easter, offsetDays));

  // --- Bundesweite Feiertage (9) ---------------------------------------------
  add(fixed(1, 1), "Neujahr", true);
  add(relEaster(-2), "Karfreitag", true);
  add(relEaster(1), "Ostermontag", true);
  add(fixed(5, 1), "Tag der Arbeit", true);
  add(relEaster(39), "Christi Himmelfahrt", true);
  add(relEaster(50), "Pfingstmontag", true);
  add(fixed(10, 3), "Tag der Deutschen Einheit", true);
  add(fixed(12, 25), "1. Weihnachtsfeiertag", true);
  add(fixed(12, 26), "2. Weihnachtsfeiertag", true);

  // --- Regionale Feiertage ----------------------------------------------------
  add(fixed(1, 6), "Heilige Drei Könige", false, ["BW", "BY", "ST"]);
  add(fixed(3, 8), "Internationaler Frauentag", false, ["BE", "MV"]);
  // Landesweiter Feiertag nur in diesen 6 Laendern (s. Modul-Kommentar oben).
  add(relEaster(60), "Fronleichnam", false, ["BW", "BY", "HE", "NW", "RP", "SL"]);
  // Landesweit nur im Saarland; in Bayern nur gemeinde-/konfessionsabhaengig (s. o.).
  add(fixed(8, 15), "Mariä Himmelfahrt", false, ["SL", "BY"]);
  // Tatsaechlich nur in der Stadt Augsburg gesetzlicher Feiertag, nicht landesweit in Bayern —
  // gleiche akzeptierte Bundesland-Ebene-Vereinfachung wie bei Fronleichnam/Mariae Himmelfahrt.
  add(fixed(8, 8), "Augsburger Friedensfest", false, ["BY"]);
  // Berlin und Bayern gehoeren NICHT dazu (haeufiger Irrtum) — seit 2018 auch HB/HH/NI/SH.
  add(fixed(10, 31), "Reformationstag", false, ["BB", "HB", "HH", "MV", "NI", "SH", "SN", "ST", "TH"]);
  add(fixed(11, 1), "Allerheiligen", false, ["BW", "BY", "NW", "RP", "SL"]);
  add(iso(bussUndBettag(year)), "Buß- und Bettag", false, ["SN"]);
  // M-N (Bughunt 2026-07-11): Weltkindertag - gesetzlicher Feiertag nur in Thueringen (seit 2019).
  add(fixed(9, 20), "Weltkindertag", false, ["TH"]);

  return list.sort((x, y) => x.date.localeCompare(y.date));
}

/* ---------------------------------------------------------------------------
 * Werktage
 *
 * WOFUER: Saetze wie „Keine Antwort seit drei Werktagen" (Entwuerfe 29a/27c,
 * docs/KONZEPT-ANFRAGEN-WER-IST-DRAN.md). Ein Angebot, das Freitagabend rausgeht, wartet am
 * Montagmorgen nicht „drei Tage" - es wartet EINEN Werktag. In Kalendertagen gerechnet wuerde die
 * Oberflaeche den Betreiber am Montag zum Nachfassen draengen, obwohl der Kunde noch gar keine
 * Gelegenheit hatte. Das ist kein Schoenheitsfehler, sondern eine falsche Handlungsaufforderung.
 *
 * WARUM HIER: Das Feiertagswissen liegt schon in diesem Paket. Eine zweite Feiertagsliste
 * anderswo waere die erste Stelle, an der die beiden auseinanderlaufen.
 *
 * ABGRENZUNG: Antwortzeiten des Betriebs werden ausdruecklich NICHT so gerechnet - dort zaehlt
 * echte Uhrzeit, auch nachts und am Wochenende (Entwurf 30b: „in echter Uhrzeit, also auch nachts
 * und am Wochenende"). Der Kunde wartet real, egal welcher Tag ist. Werktage sind das Mass fuer
 * das, was man vom KUNDEN erwarten darf, nicht fuer das, was der Betrieb sich zugesteht.
 *
 * ENTSCHIEDEN (BUGHUNT-2026-08-15 Q1, Antoine): auch die Nachfass-FRISTEN rechnen in WERKTAGEN,
 * Berlin-verankert. Die SQL-Funktion des Sweeps (sql/66 bzw. 277) liefert nur noch Kandidaten
 * nach Kalendertagen (eine Obermenge); die Entscheidung faellt im Worker ueber
 * `nextFollowUpStage` (apps/api/src/lib/followup.ts), das `werktageSeit` aus diesem Paket nutzt.
 * ------------------------------------------------------------------------- */

/** Sa/So? Nutzt UTC, weil `iso()` und die gesamte Feiertagsliste in UTC arbeiten. */
function istWochenende(d: Date): boolean {
  const wt = d.getUTCDay();
  return wt === 0 || wt === 6;
}

/**
 * Zaehlt die VOLLEN Werktage zwischen zwei Zeitpunkten - ohne Sa/So und ohne die gesetzlichen
 * Feiertage des Bundeslands.
 *
 * Gezaehlt werden die Tage NACH `von` bis einschliesslich `bis`. „Freitag verschickt, Montag
 * geschaut" ergibt damit 1: der Freitag selbst zaehlt nicht mit (da wurde ja gerade erst
 * gesendet), Sa/So zaehlen nicht, der Montag zaehlt.
 *
 * `bis` vor oder gleich `von` ergibt 0 - eine negative Wartezeit gibt es nicht, und ein
 * verdrehtes Argumentpaar darf keinen absurden Satz erzeugen.
 *
 * Unbekanntes/leeres Bundesland: nur bundesweite Feiertage (Verhalten von `germanHolidays`,
 * bewusst kein Fehler - lieber leicht zu hoch gezaehlt als ein Absturz in einer Textzeile).
 */
export function werktageZwischen(von: Date, bis: Date, bundesland: string): number {
  if (!(von instanceof Date) || !(bis instanceof Date)) return 0;
  if (Number.isNaN(von.getTime()) || Number.isNaN(bis.getTime())) return 0;
  if (bis.getTime() <= von.getTime()) return 0;

  // Auf Tagesgrenzen (UTC) normieren: gezaehlt werden Tage, nicht Stunden.
  const tag = (d: Date) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const start = tag(von);
  const ende = tag(bis);
  if (ende <= start) return 0;

  // Feiertage aller beruehrten Jahre EINMAL einsammeln (der Zeitraum kann ueber den
  // Jahreswechsel gehen - sonst faellt Neujahr durch das Raster).
  const jahre = new Set<number>();
  for (let j = new Date(start).getUTCFullYear(); j <= new Date(ende).getUTCFullYear(); j++) jahre.add(j);
  const feiertage = new Set<string>();
  for (const j of jahre) for (const f of germanHolidays(j, bundesland)) feiertage.add(f.date);

  let zahl = 0;
  for (let t = start + 86_400_000; t <= ende; t += 86_400_000) {
    const d = new Date(t);
    if (istWochenende(d)) continue;
    if (feiertage.has(iso(d))) continue;
    zahl++;
  }
  return zahl;
}

/* ---------------------------------------------------------------------------
 * Zonen-verankerte Kalendertage (BUGHUNT-2026-08-15 Q1/Q2/Q6/Q13)
 *
 * WOFUER: Jede fachliche Datumsgrenze in MAESTRO liegt in Europe/Berlin (sql/85, N-9) — ein
 * Angebot, das am 3.8. um 23:30 UTC rausgeht, ist in Berlin schon am 4.8. verschickt worden.
 * Wer stattdessen mit UTC-Tagesgrenzen rechnet (`toISOString().slice(0, 10)` oder rohe
 * Timestamps an `werktageZwischen`), zaehlt an genau diesen Raendern einen Tag daneben.
 *
 * WARUM HIER: `werktageZwischen` liegt schon in diesem Paket, und die Anzeige
 * (`@maestro/anfrage-stand`) hatte die Zonen-Verankerung bereits — als lokale Funktion. Der
 * Bughunt fand drei weitere Stellen, die dieselbe Groesse OHNE Verankerung rechneten
 * (lib/followup.ts, routes/tasks.ts, lib/reminder-sweep.ts). Eine zweite/dritte Fassung
 * anderswo waere die erste Stelle, an der die Fristen wieder auseinanderlaufen; deshalb wandert
 * die EINE Fassung hierher, und `@maestro/anfrage-stand` reicht sie nur noch durch.
 * ------------------------------------------------------------------------- */

/**
 * Der Kalendertag eines Zeitpunkts in der gewuenschten Zone als ISO-Datum (`YYYY-MM-DD`).
 * Ueber `formatToParts` statt ueber eine eigene Sommerzeit-Rechnung: die Umstellungsregel steht
 * bereits in der Laufzeit, eine zweite Fassung hier waere die erste Stelle, an der beide
 * auseinanderlaufen. Ungueltige Zeitpunkte ergeben `null`, keinen Absturz.
 */
export function isoTagInZone(zeitpunkt: Date, zeitzone: string): string | null {
  if (!(zeitpunkt instanceof Date) || !Number.isFinite(zeitpunkt.getTime())) return null;
  const teile = new Intl.DateTimeFormat("en-CA", {
    timeZone: zeitzone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(zeitpunkt);
  const lies = (art: string) => teile.find((t) => t.type === art)?.value ?? null;
  const jahr = lies("year");
  const monat = lies("month");
  const tag = lies("day");
  if (!jahr || !monat || !tag) return null;
  return `${jahr}-${monat}-${tag}`;
}

/**
 * „Heute" als ISO-Datum in der Zone — die EINE Definition fuer „ueberfaellig"/„heute faellig"
 * im Worker-Code, exakt wie `(now() at time zone 'Europe/Berlin')::date` in SQL (sql/85,
 * lib/kundenhistorie.ts, lib/anfragen-abfrage.ts). `jetzt` ist injizierbar fuer Tests; der
 * UTC-Fallback ist reine Typ-Beruhigung — fuer ein echtes Datum und eine gueltige Zone greift
 * er nie.
 */
export function heuteInZone(zeitzone: string, jetzt: Date = new Date()): string {
  return isoTagInZone(jetzt, zeitzone) ?? jetzt.toISOString().slice(0, 10);
}

/** UTC-Mitternacht des Kalendertages in der Zone — der Anker fuer `werktageZwischen`. */
function tagesAnkerInZone(zeitpunkt: Date, zeitzone: string): Date | null {
  const tag = isoTagInZone(zeitpunkt, zeitzone);
  return tag ? new Date(`${tag}T00:00:00.000Z`) : null;
}

/**
 * Volle Werktage zwischen zwei Zeitpunkten, gerechnet ueber die Kalendertage der Zone.
 *
 * `werktageZwischen` normiert auf UTC-Tagesgrenzen — deshalb bekommt es hier **Tagesanker**
 * (UTC-Mitternacht des zonalen Kalendertages) statt der rohen Zeitstempel. Ein Versand um
 * 00:30 Berliner Sommerzeit liegt in UTC noch am Vortag; ohne Anker zaehlte die Frist ab dem
 * falschen Tag (Q6). Bisher lag diese Funktion in `@maestro/anfrage-stand` (nur Anzeige) —
 * jetzt rechnen Anzeige UND Fristen (lib/followup.ts) mit derselben.
 */
export function werktageSeit(von: Date, jetzt: Date, bundesland: string, zeitzone: string): number {
  const a = tagesAnkerInZone(von, zeitzone);
  const b = tagesAnkerInZone(jetzt, zeitzone);
  if (!a || !b) return 0;
  return werktageZwischen(a, b, bundesland);
}

/**
 * Der n-te Werktag NACH einem Kalendertag (`YYYY-MM-DD`): Montag bis Freitag, gesetzliche Feiertage
 * des Bundeslandes uebersprungen. Der Tag selbst zaehlt nie mit.
 *
 * WOFUER: Versandtag der Google-Bewertungsbitte (docs/KONZEPT-GOOGLE-BEWERTUNG.md, Antoine
 * 26.09.2026: „Dritter Werktag nach dem Veranstaltungstag … Fr-Feier → Mi“). Liefert `null` bei
 * einem ungueltigen Datum oder n < 1 — lieber kein Versandtag als ein erfundener.
 */
export function naechsterWerktag(tag: string, n: number, bundesland: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(tag) || !Number.isInteger(n) || n < 1) return null;
  let d = new Date(`${tag}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  const feiertage = new Map<number, Set<string>>();
  const istFeiertag = (x: Date) => {
    const j = x.getUTCFullYear();
    if (!feiertage.has(j)) feiertage.set(j, new Set(germanHolidays(j, bundesland).map((f) => f.date)));
    return feiertage.get(j)!.has(iso(x));
  };
  let gezaehlt = 0;
  while (gezaehlt < n) {
    d = addDays(d, 1);
    if (!istWochenende(d) && !istFeiertag(d)) gezaehlt++;
  }
  return iso(d);
}

/**
 * Datums-Plausibilitaet (V-1304, Mimmo 02.11.2026): ein Veranstaltungsdatum in der Vergangenheit
 * ist bei einem NEUEN Vorgang/Angebot fast immer ein Tippfehler im Jahr („04.12.20" statt
 * „04.12.2026"). Die EINE Regel fuer Server (Versand-Rueckfrage) und Oberflaeche (Hinweis im
 * Anlege-Formular, Sende-Dialog, „Datum pruefen" in Listen). Keine Sperre: echte Altfaelle
 * (Nachtraege zu vergangenen Veranstaltungen) bleiben moeglich, sie brauchen nur eine Bestaetigung.
 *
 * `null` = Datum fehlt, ist ungueltig oder liegt heute/in der Zukunft — nichts zu pruefen.
 * Sonst `{ datum, vorschlag }`: `vorschlag` ist das naechste Vorkommen desselben Tages ab heute
 * (29.02. springt bis zum naechsten Schaltjahr), damit die Rueckfrage eine Antwort anbieten kann
 * („meintest du 04.12.2026?").
 */
export interface DatumInVergangenheit {
  /** Das gespeicherte (Start-)Datum (ISO YYYY-MM-DD). */
  datum: string;
  /** Naechstes Vorkommen von Tag+Monat ab `heute` (ISO), `null` wenn keins gefunden. Bei
   *  mehrtaegigen Veranstaltungen: der um dieselbe Jahresdifferenz verschobene Start. */
  vorschlag: string | null;
  /** Enddatum (ISO) einer mehrtaegigen Veranstaltung, sonst `null`. */
  ende: string | null;
  /** Der um dieselbe Jahresdifferenz verschobene Ende-Vorschlag (nur mit `ende` und `vorschlag`). */
  vorschlagEnde: string | null;
}

function isoTag(wert: string | null | undefined): { j: number; m: number; t: number; iso: string } | null {
  const m = (wert ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? { j: Number(m[1]), m: Number(m[2]), t: Number(m[3]), iso: `${m[1]}-${m[2]}-${m[3]}` } : null;
}

function mitJahr(d: { m: number; t: number }, jahr: number): string | null {
  const x = new Date(Date.UTC(jahr, d.m - 1, d.t));
  return x.getUTCMonth() === d.m - 1 ? x.toISOString().slice(0, 10) : null; // 29.02. nur im Schaltjahr
}

/**
 * Massgeblich ist das ENDE (`endeIso`, falls gesetzt, sonst der Start) — dieselbe Regel wie der Server
 * (`dateEnd ?? eventDate`): eine Veranstaltung, die gestern begann und morgen endet, ist nicht vergangen.
 * Der Vorschlag verschiebt Start UND Ende um dieselbe Jahresdifferenz, damit Start <= Ende bleibt.
 */
export function datumInVergangenheit(datumIso: string | null | undefined, heuteIso: string, endeIso?: string | null): DatumInVergangenheit | null {
  const start = isoTag(datumIso);
  if (!start) return null;
  const ende = isoTag(endeIso);
  const heute = heuteIso.slice(0, 10);
  const referenz = ende ?? start;
  if (referenz.iso >= heute) return null;
  const startJahr = Number(heute.slice(0, 4));
  let vorschlag: string | null = null;
  let vorschlagEnde: string | null = null;
  for (let j = startJahr; j <= startJahr + 8; j++) {
    const kandidatRef = mitJahr(referenz, j);
    if (!kandidatRef || kandidatRef < heute) continue;
    const delta = j - referenz.j;
    const kandidatStart = mitJahr(start, start.j + delta);
    if (!kandidatStart) continue;
    vorschlag = kandidatStart;
    vorschlagEnde = ende ? kandidatRef : null;
    break;
  }
  return { datum: start.iso, vorschlag, ende: ende ? ende.iso : null, vorschlagEnde };
}
